import React, { useState, useEffect } from 'react';
import axios from 'axios';
import {
  BarChart,
  Bar,
  LineChart,
  Line,
  PieChart,
  Pie,
  Cell,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
  ResponsiveContainer,
} from 'recharts';
import './Statistics.css';

const server_url = process.env.REACT_APP_API_URI;

const COLORS = [
  '#10b981',
  '#ff9933',
  '#3b82f6',
  '#f59e0b',
  '#8b5cf6',
  '#ec4899',
];

function Statistics({ userId }) {
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState(0);
  const [stats, setStats] = useState(null);
  const [categoryBreakdown, setCategoryBreakdown] = useState([]);
  const [heatmap, setHeatmap] = useState([]);
  const [pointsTrend, setPointsTrend] = useState([]);
  const [ranking, setRanking] = useState(null);
  const [milestones, setMilestones] = useState([]);

  useEffect(() => {
    if (userId) {
      fetchAllStatistics();
    }
  }, [userId]);

  const fetchAllStatistics = async () => {
    setLoading(true);
    try {
      const [
        statsRes,
        categoryRes,
        heatmapRes,
        trendRes,
        rankingRes,
        milestonesRes,
      ] = await Promise.all([
        axios.post(`${server_url}/user/statistics`, { userId }),
        axios.post(`${server_url}/user/statistics/category-breakdown`, {
          userId,
        }),
        axios.post(`${server_url}/user/statistics/activity-heatmap`, {
          userId,
        }),
        axios.post(`${server_url}/user/statistics/points-trend`, { userId }),
        axios.post(`${server_url}/user/statistics/ranking`, { userId }),
        axios.post(`${server_url}/user/statistics/milestones`, { userId }),
      ]);

      setStats(statsRes.data);
      setCategoryBreakdown(categoryRes.data.categories);
      setHeatmap(heatmapRes.data.heatmap);
      setPointsTrend(trendRes.data.trend);
      setRanking(rankingRes.data);
      setMilestones(milestonesRes.data.milestones);
    } catch (error) {
      console.error('Error fetching statistics:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleTabChange = (newValue) => {
    setActiveTab(newValue);
  };

  if (loading) {
    return (
      <div className="flex justify-center items-center p-16">
        <div className="animate-spin rounded-full h-16 w-16 border-t-2 border-b-2 border-green-500"></div>
      </div>
    );
  }

  if (!stats) {
    return (
      <div className="p-8 text-center">
        <p className="text-gray-400 text-lg">No statistics available</p>
      </div>
    );
  }

  return (
    <div className="statistics-container px-3 pb-8">
      <h2 className="text-3xl font-bold text-secondary text-center mb-6">
        Statistics Dashboard
      </h2>

      {/* Overview Cards */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 mb-6">
        <div className="stat-card bg-gradient-to-br from-green-500 to-emerald-600">
          <div className="flex items-center mb-2">
            <svg
              className="w-6 h-6 mr-2"
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M11.049 2.927c.3-.921 1.603-.921 1.902 0l1.519 4.674a1 1 0 00.95.69h4.915c.969 0 1.371 1.24.588 1.81l-3.976 2.888a1 1 0 00-.363 1.118l1.518 4.674c.3.922-.755 1.688-1.538 1.118l-3.976-2.888a1 1 0 00-1.176 0l-3.976 2.888c-.783.57-1.838-.197-1.538-1.118l1.518-4.674a1 1 0 00-.363-1.118l-3.976-2.888c-.784-.57-.38-1.81.588-1.81h4.914a1 1 0 00.951-.69l1.519-4.674z"
              />
            </svg>
            <span className="text-sm font-medium">Total Points</span>
          </div>
          <p className="text-4xl font-bold">{stats.totalPoints}</p>
        </div>

        <div className="stat-card bg-gradient-to-br from-orange-500 to-orange-600">
          <div className="flex items-center mb-2">
            <svg
              className="w-6 h-6 mr-2"
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M13 7h8m0 0v8m0-8l-8 8-4-4-6 6"
              />
            </svg>
            <span className="text-sm font-medium">Total Reviews</span>
          </div>
          <p className="text-4xl font-bold">{stats.totalReviews}</p>
        </div>

        <div className="stat-card bg-gradient-to-br from-blue-500 to-cyan-600">
          <div className="flex items-center mb-2">
            <svg
              className="w-6 h-6 mr-2"
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M17.657 18.657A8 8 0 016.343 7.343S7 9 9 10c0-2 .5-5 2.986-7C14 5 16.09 5.777 17.656 7.343A7.975 7.975 0 0120 13a7.975 7.975 0 01-2.343 5.657z"
              />
            </svg>
            <span className="text-sm font-medium">Current Streak</span>
          </div>
          <p className="text-4xl font-bold">{stats.currentStreak}</p>
          <span className="text-xs opacity-90">days</span>
        </div>

        <div className="stat-card bg-gradient-to-br from-yellow-500 to-orange-500">
          <div className="flex items-center mb-2">
            <svg
              className="w-6 h-6 mr-2"
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M12 4.354a4 4 0 110 5.292M15 21H3v-1a6 6 0 0112 0v1zm0 0h6v-1a6 6 0 00-9-5.197M13 7a4 4 0 11-8 0 4 4 0 018 0z"
              />
            </svg>
            <span className="text-sm font-medium">Referrals</span>
          </div>
          <p className="text-4xl font-bold">{stats.referralCount}</p>
        </div>
      </div>

      {/* Ranking Cards */}
      {ranking && (
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-6">
          <div className="dark-card">
            <h3 className="text-xl font-bold text-secondary mb-4">
              Points Ranking
            </h3>
            <div className="flex items-center justify-between">
              <div>
                <p className="text-5xl font-bold text-green-400">
                  #{ranking.pointsRank?.rank}
                </p>
                <p className="text-gray-400 mt-1">
                  out of {ranking.pointsRank?.totalUsers} users
                </p>
              </div>
              <div className="badge-chip">
                Top {ranking.pointsRank?.percentile}%
              </div>
            </div>
          </div>

          <div className="dark-card">
            <h3 className="text-xl font-bold text-secondary mb-4">
              Reviews Ranking
            </h3>
            <div className="flex items-center justify-between">
              <div>
                <p className="text-5xl font-bold text-orange-400">
                  #{ranking.reviewsRank?.rank}
                </p>
                <p className="text-gray-400 mt-1">
                  out of {ranking.reviewsRank?.totalUsers} users
                </p>
              </div>
              <div className="badge-chip">
                Top {ranking.reviewsRank?.percentile}%
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Tabs */}
      <div className="tabs-container mb-6">
        <button
          onClick={() => handleTabChange(0)}
          className={`tab-button ${activeTab === 0 ? 'active' : ''}`}
        >
          Overview
        </button>
        <button
          onClick={() => handleTabChange(1)}
          className={`tab-button ${activeTab === 1 ? 'active' : ''}`}
        >
          Activity
        </button>
        <button
          onClick={() => handleTabChange(2)}
          className={`tab-button ${activeTab === 2 ? 'active' : ''}`}
        >
          Achievements
        </button>
      </div>

      {/* Tab Content */}
      {activeTab === 0 && (
        <div className="space-y-6">
          {/* Category Breakdown */}
          {categoryBreakdown.length > 0 && (
            <div className="dark-card">
              <h3 className="text-xl font-bold text-secondary mb-4">
                Reviews by Category
              </h3>
              <ResponsiveContainer width="100%" height={300}>
                <PieChart>
                  <Pie
                    data={categoryBreakdown}
                    dataKey="count"
                    nameKey="category"
                    cx="50%"
                    cy="50%"
                    outerRadius={100}
                    label={(entry) => `${entry.category}: ${entry.count}`}
                    labelLine={false}
                  >
                    {categoryBreakdown.map((entry, index) => (
                      <Cell
                        key={`cell-${index}`}
                        fill={COLORS[index % COLORS.length]}
                      />
                    ))}
                  </Pie>
                  <Tooltip
                    contentStyle={{
                      backgroundColor: '#1f2937',
                      border: '1px solid #374151',
                      color: '#fff',
                    }}
                  />
                  <Legend wrapperStyle={{ color: '#9ca3af' }} />
                </PieChart>
              </ResponsiveContainer>
            </div>
          )}

          {/* Points Trend */}
          {pointsTrend.length > 0 && (
            <div className="dark-card">
              <h3 className="text-xl font-bold text-secondary mb-4">
                Points Over Time
              </h3>
              <ResponsiveContainer width="100%" height={300}>
                <LineChart data={pointsTrend}>
                  <CartesianGrid strokeDasharray="3 3" stroke="#374151" />
                  <XAxis
                    dataKey="month"
                    stroke="#9ca3af"
                    style={{ fontSize: '12px' }}
                  />
                  <YAxis stroke="#9ca3af" style={{ fontSize: '12px' }} />
                  <Tooltip
                    contentStyle={{
                      backgroundColor: '#1f2937',
                      border: '1px solid #374151',
                      color: '#fff',
                    }}
                  />
                  <Legend wrapperStyle={{ color: '#9ca3af' }} />
                  <Line
                    type="monotone"
                    dataKey="pointsEarned"
                    stroke="#10b981"
                    name="Points Earned"
                    strokeWidth={3}
                  />
                  <Line
                    type="monotone"
                    dataKey="activities"
                    stroke="#ff9933"
                    name="Activities"
                    strokeWidth={3}
                  />
                </LineChart>
              </ResponsiveContainer>
            </div>
          )}
        </div>
      )}

      {activeTab === 1 && (
        <div className="dark-card">
          <h3 className="text-xl font-bold text-secondary mb-4">
            Activity Heatmap
          </h3>
          {heatmap.length > 0 ? (
            <div>
              <p className="text-gray-400 text-sm mb-4">
                Your activity over the past year
              </p>
              <div className="heatmap-grid">
                {heatmap.map((day, index) => (
                  <div
                    key={index}
                    className="heatmap-cell"
                    style={{
                      backgroundColor:
                        day.count === 0
                          ? '#1f2937'
                          : day.count <= 2
                            ? '#10b981'
                            : day.count <= 4
                              ? '#059669'
                              : day.count <= 6
                                ? '#047857'
                                : '#065f46',
                    }}
                    title={`${day.date}: ${day.count} activities`}
                  />
                ))}
              </div>
              <div className="flex items-center gap-2 mt-4 text-xs text-gray-400">
                <span>Less</span>
                {[0, 1, 2, 3, 4].map((level) => (
                  <div
                    key={level}
                    className="heatmap-cell"
                    style={{
                      backgroundColor:
                        level === 0
                          ? '#1f2937'
                          : level === 1
                            ? '#10b981'
                            : level === 2
                              ? '#059669'
                              : level === 3
                                ? '#047857'
                                : '#065f46',
                    }}
                  />
                ))}
                <span>More</span>
              </div>
            </div>
          ) : (
            <p className="text-gray-400">No activity data available</p>
          )}
        </div>
      )}

      {activeTab === 2 && (
        <div className="dark-card">
          <h3 className="text-xl font-bold text-secondary mb-4">
            Achievements & Milestones
          </h3>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
            {milestones
              .filter((m) => m.achieved || m.progress > 0)
              .sort((a, b) => b.progress - a.progress)
              .slice(0, 12)
              .map((milestone, index) => (
                <div
                  key={index}
                  className={`milestone-card ${milestone.achieved ? 'achieved' : ''}`}
                >
                  <div className="flex items-center justify-between mb-2">
                    <span className="font-bold text-sm text-white">
                      {milestone.name}
                    </span>
                    {milestone.achieved && (
                      <svg
                        className="w-5 h-5 text-yellow-400"
                        fill="currentColor"
                        viewBox="0 0 20 20"
                      >
                        <path d="M9.049 2.927c.3-.921 1.603-.921 1.902 0l1.07 3.292a1 1 0 00.95.69h3.462c.969 0 1.371 1.24.588 1.81l-2.8 2.034a1 1 0 00-.364 1.118l1.07 3.292c.3.921-.755 1.688-1.54 1.118l-2.8-2.034a1 1 0 00-1.175 0l-2.8 2.034c-.784.57-1.838-.197-1.539-1.118l1.07-3.292a1 1 0 00-.364-1.118L2.98 8.72c-.783-.57-.38-1.81.588-1.81h3.461a1 1 0 00.951-.69l1.07-3.292z" />
                      </svg>
                    )}
                  </div>
                  <div className="progress-bar-container">
                    <div
                      className="progress-bar"
                      style={{ width: `${milestone.progress}%` }}
                    />
                  </div>
                  <div className="flex items-center justify-between mt-2">
                    <span className="text-xs text-gray-400">
                      {milestone.current} / {milestone.threshold}
                    </span>
                    <span className="text-xs font-semibold text-green-400">
                      {Math.round(milestone.progress)}%
                    </span>
                  </div>
                </div>
              ))}
          </div>
        </div>
      )}
    </div>
  );
}

export default Statistics;
